// Firebase Auth wiring. Config comes from Vite env vars at build time:
//   VITE_FIREBASE_API_KEY
//   VITE_FIREBASE_AUTH_DOMAIN
//   VITE_FIREBASE_PROJECT_ID
//   VITE_FIREBASE_APP_ID
//
// When any of these is missing (dev mode without Firebase set up), `auth` is
// null and the app falls back to the stub picker.

import { initializeApp, type FirebaseApp } from "firebase/app";
import {
  getAuth, GoogleAuthProvider, signInWithPopup, signOut, onAuthStateChanged,
  type User, type Auth,
} from "firebase/auth";

const cfg = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

const enabled = Boolean(cfg.apiKey && cfg.authDomain && cfg.projectId);

let app: FirebaseApp | null = null;
let auth: Auth | null = null;
if (enabled) {
  app = initializeApp(cfg);
  auth = getAuth(app);
}

export function firebaseEnabled() { return enabled; }

// Popup-based Google sign-in. This is Firebase's default, simplest path and
// works on all regular browsers (desktop + mobile Chrome/Safari/Firefox).
// In-app browsers (WhatsApp, iMessage, etc.) block popups — see the hint on
// the Login screen telling those users to open in their default browser.
export async function signInWithGoogle(): Promise<User> {
  if (!auth) throw new Error("Firebase not configured");
  const provider = new GoogleAuthProvider();
  const result = await signInWithPopup(auth, provider);
  return result.user;
}

export async function signOutUser() {
  if (!auth) return;
  await signOut(auth);
}

export function onAuthChanged(cb: (user: User | null) => void) {
  if (!auth) { cb(null); return () => {}; }
  return onAuthStateChanged(auth, cb);
}

export async function getIdToken(): Promise<string | null> {
  if (!auth?.currentUser) return null;
  return auth.currentUser.getIdToken();
}
